import React, { useEffect, useRef } from 'react';
import { Bar } from 'rough-viz';

// Module-scoped counter so each <RoughBar/> mounted in the app gets a
// unique DOM id without relying on useId (React 18). The id is stable
// for the lifetime of the component because we cache it in a ref.
let nextRoughBarId = 0;

type Props = {
  labels: string[];
  values: number[];
  title?: string;
  xLabel?: string;
  yLabel?: string;
  /** A rough-viz colour palette name OR an array of hex/css colours. */
  color?: string | string[];
  /** Drawing "roughness" (1 = clean, higher = scribblier). */
  roughness?: number;
  /** Hand-drawn font keyword rough-viz accepts. 0 = Gaegu, 1 = Indie Flower. */
  fontStyle?: 0 | 1;
  /** Pixel width; rough-viz takes a fixed number, not a CSS dimension. */
  width?: number;
  height?: number;
  /** Bottom margin in px. rough-viz rotates the x-axis tick labels -45°,
   *  so long category names need headroom below the axis or they clip off
   *  the bottom of the SVG. Default 70; bump it for long labels. */
  marginBottom?: number;
  /** Font size for axis tick + title labels (CSS dimension, e.g. "1rem"). */
  axisFontSize?: string;
  /** Font size for hover-tooltip labels (CSS dimension, e.g. "1rem"). */
  tooltipFontSize?: string;
  /** Called with the bar's index (aligned to `labels`/`values`) when a bar
   *  is clicked. When set, bars get a pointer cursor. Omit for a static
   *  chart. */
  onBarClick?: (index: number) => void;
};

const RoughBar: React.FC<Props> = ({
  labels,
  values,
  title,
  xLabel,
  yLabel,
  color,
  roughness,
  fontStyle,
  width = 700,
  height = 350,
  marginBottom = 70,
  axisFontSize,
  tooltipFontSize,
  onBarClick,
}) => {
  // Stable, render-unique selector — rough-viz needs a real DOM id
  // (not a class or ref) for its `element` option. useRef caches the
  // id for the component's lifetime so re-renders don't bump it.
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = `rough-bar-${++nextRoughBarId}`;
  }
  const id = idRef.current;
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    // Capture for the cleanup closure — ref.current may point at a
    // different node by the time React tears the effect down.
    const host = ref.current;
    host.innerHTML = '';

    try {
      new Bar({
        element: `#${id}`,
        data: { labels, values },
        title,
        xLabel,
        yLabel,
        color,
        roughness,
        fillStyle: 'hachure',
        fillWeight: 1.5,
        stroke: '#1f2937',
        strokeWidth: 1.2,
        font: fontStyle,
        width,
        height,
        labels: 'labels',
        values: 'values',
        axisFontSize,
        tooltipFontSize,
        margin: { top: 50, right: 20, bottom: marginBottom, left: 80 },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[RoughBar] construct failed', err, {
        id,
        labels,
        values,
      });
    }

    // rough-viz renders each bar as an <g class="{id}" attrX="..."> in data
    // order. We map a click to a bar index via DELEGATION on the stable
    // host div rather than per-<g> listeners: rough-viz redraws its bars
    // in place on window resize (it's `responsive`), which would silently
    // drop listeners bound directly to the old <g> nodes. The host div
    // survives those redraws, so the handler keeps working.
    let clickHandler: ((e: Event) => void) | null = null;
    if (onBarClick) {
      host
        .querySelectorAll<SVGGElement>(`g.${CSS.escape(id)}`)
        .forEach((g) => {
          g.style.cursor = 'pointer';
        });
      clickHandler = (e: Event) => {
        // Walk up from the click target to the bar <g> (class === id,
        // carries attrX); ignore clicks that miss a bar (axes, gaps).
        let el = e.target as Element | null;
        while (el && el !== host) {
          if (el.getAttribute?.('attrX') !== null && el.classList?.contains(id)) {
            break;
          }
          el = el.parentElement;
        }
        if (!el || el === host) return;
        const bars = Array.from(host.querySelectorAll(`g.${CSS.escape(id)}`));
        const idx = bars.indexOf(el);
        if (idx >= 0) onBarClick(idx);
      };
      host.addEventListener('click', clickHandler);
    }

    return () => {
      if (clickHandler) host.removeEventListener('click', clickHandler);
      host.innerHTML = '';
    };
  }, [
    onBarClick,
    id,
    labels,
    values,
    title,
    xLabel,
    yLabel,
    color,
    roughness,
    fontStyle,
    width,
    height,
    marginBottom,
    axisFontSize,
    tooltipFontSize,
  ]);

  return (
    <div
      id={id}
      ref={ref}
      // Force a minimum size so the host div can't be collapsed by
      // surrounding flex/grid layout — rough-viz appends an SVG sized
      // by the `width`/`height` options, but if the parent is 0×0 the
      // SVG ends up invisible in some browsers.
      //
      // `position: relative` makes this div the containing block for
      // rough-viz's absolutely-positioned tooltip. Without it the tooltip
      // resolves against the viewport, so once the chart is scrolled
      // horizontally (inside an overflow-x container) the tooltip for a
      // right-side bar lands far off-screen. Anchored here it tracks the
      // scrolled content and stays over the hovered bar.
      style={{ position: 'relative', minWidth: width, minHeight: height }}
    />
  );
};

export default RoughBar;
